import { useState } from 'react';
import type { ContactRole, StudioCard, Warning } from '@/api/base-types';
import { useBaseMutation, useStudioList, useTeamList } from '@/api/base';
import { api } from '@/api/client';
import { ChoiceField, Option, TextLink } from '@/components/kit/Controls';
import { TextField } from '@/components/kit/Field';
import { Modal } from '@/components/kit/Modal';
import { FieldHint, OptionList } from '@/components/kit/OptionList';
import { Note } from '@/components/kit/Note';
import { useToast } from '@/components/kit/Toast';
import { normalizeName } from '@/lib/text';
import { CityField, type CityValue } from '../shared/CityField';
import { ClientPicker, emptyPicker, pickerPayload, pickerReady, type PickerState } from '../shared/ClientPicker';
import { useDebounced } from '../shared/EntityPickers';
import { CONTACT_ROLE_ITEMS } from '../shared/labels';
import { errorText } from '../shared/useUndoable';

const yearError = (y: string) => (y && !/^\d{4}$/.test(y) ? 'Четыре цифры' : null);

/** Студия с тем же названием в том же городе — подсказка о дубле (БЛ 5.4). */
function useStudioTwin(name: string, cityId: string | undefined, exceptId?: string) {
  const list = useStudioList({ q: name, cityId, limit: 5 }, name.trim().length >= 2 && Boolean(cityId));
  return (list.data?.items ?? []).find(
    (s) => s.id !== exceptId && s.city.id === cityId && normalizeName(s.name) === normalizeName(name),
  );
}

/** Новая студия (fNewStudio): название, город, год; основной контакт — сразу или позже. */
export function NewStudioModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [city, setCity] = useState<CityValue | null>(null);
  const [year, setYear] = useState('');
  const [contact, setContact] = useState<PickerState>(emptyPicker(true));
  const [role, setRole] = useState<ContactRole>('head');
  const twin = useStudioTwin(name, city?.id);

  const create = useBaseMutation(() =>
    api.post<{ id: string; warnings: Warning[] }>('/studios', {
      name,
      cityId: city?.id,
      foundedYear: year ? Number(year) : null,
      ...(contact.mode !== 'later' ? { contact: { client: pickerPayload(contact, 'phone'), role } } : {}),
    }),
  );

  const ready = name.trim() && city && !yearError(year) && pickerReady(contact, 'phone') && !create.isPending;

  return (
    <Modal
      title="Новая студия"
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Создать студию',
        disabled: !ready,
        onClick: () =>
          create.mutate(undefined, {
            onSuccess: (r) => {
              toast.show('Студия создана');
              onClose();
              onCreated(r.id);
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <div className="row keep">
        <div className="col">
          <TextField label="Название" value={name} onValue={setName} autoFocus />
        </div>
        <div className="col">
          <CityField inModal value={city} onChange={setCity} />
        </div>
      </div>
      {twin ? (
        <div className="mnote">
          <Note tone="warn">
            <b>Похоже на дубль:</b> студия «{twin.name}» в этом городе уже есть. Создать можно, но проверьте.
          </Note>
        </div>
      ) : null}
      <TextField
        label="Год основания"
        placeholder="ГГГГ, необязательно"
        inputMode="numeric"
        value={year}
        onValue={setYear}
        error={yearError(year)}
      />
      <ClientPicker label="Основной контакт" need="phone" allowLater value={contact} onChange={setContact} />
      {contact.mode !== 'later' ? (
        <ChoiceField label="Роль в студии" items={CONTACT_ROLE_ITEMS} value={role} onChange={setRole} />
      ) : null}
    </Modal>
  );
}

/** Изменить данные студии: название, город, год основания. */
export function EditStudioModal({ studio, onClose }: { studio: StudioCard; onClose: () => void }) {
  const toast = useToast();
  const [name, setName] = useState(studio.name);
  const [city, setCity] = useState<CityValue | null>(studio.city);
  const [year, setYear] = useState(studio.foundedYear ? String(studio.foundedYear) : '');
  const [instagram, setInstagram] = useState(studio.instagram ?? '');
  const twin = useStudioTwin(name, city?.id, studio.id);
  const save = useBaseMutation(() =>
    api.patch(`/studios/${studio.id}`, {
      name,
      cityId: city?.id,
      foundedYear: year ? Number(year) : null,
      instagram: instagram.trim() || null,
    }),
  );
  return (
    <Modal
      title="Изменить данные студии"
      onClose={onClose}
      confirm={{
        label: 'Сохранить',
        disabled: !name.trim() || !city || Boolean(yearError(year)) || save.isPending,
        onClick: () =>
          save.mutate(undefined, {
            onSuccess: () => {
              toast.show('Данные сохранены');
              onClose();
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <div className="row keep">
        <div className="col">
          <TextField label="Название" value={name} onValue={setName} />
        </div>
        <div className="col">
          <CityField inModal value={city} onChange={setCity} />
        </div>
      </div>
      {twin ? (
        <div className="mnote">
          <Note tone="warn">
            <b>Похоже на дубль:</b> студия «{twin.name}» в этом городе уже есть.
          </Note>
        </div>
      ) : null}
      <div className="row keep">
        <div className="col">
          <TextField
            label="Год основания"
            placeholder="ГГГГ"
            inputMode="numeric"
            value={year}
            onValue={setYear}
            error={yearError(year)}
          />
        </div>
        <div className="col">
          <TextField label="Instagram" placeholder="ник без @" value={instagram} onValue={setInstagram} />
        </div>
      </div>
    </Modal>
  );
}

/**
 * Добавить контакт студии (fContact) / Привязать к студии с карточки
 * клиента: клиент с телефоном, роль, основной ли. Первый контакт
 * становится основным сам (ТЗ 6.9).
 */
export function AddContactModal({ studio, onClose }: { studio: StudioCard; onClose: () => void }) {
  const toast = useToast();
  const [client, setClient] = useState<PickerState>(emptyPicker());
  const [role, setRole] = useState<ContactRole>('head');
  const [isMain, setIsMain] = useState(false);
  const main = studio.contacts.find((c) => c.isMain);
  const add = useBaseMutation(() =>
    api.post(`/studios/${studio.id}/contacts`, { client: pickerPayload(client, 'phone'), role, isMain }),
  );
  return (
    <Modal
      title={`Добавить контакт студии «${studio.name}»`}
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Добавить контакт',
        disabled: !pickerReady(client, 'phone') || add.isPending,
        onClick: () =>
          add.mutate(undefined, {
            onSuccess: () => {
              toast.show('Контакт добавлен');
              onClose();
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <ClientPicker
        label="Контакт"
        need="phone"
        value={client}
        onChange={setClient}
        exclude={studio.contacts.map((c) => c.client.id)}
      />
      <ChoiceField label="Роль в студии" items={CONTACT_ROLE_ITEMS} value={role} onChange={setRole} />
      {main ? (
        <Option
          checked={isMain}
          onToggle={() => setIsMain(!isMain)}
          title="Сделать основным контактом"
          description={`признак снимется с ${main.client.shortName}`}
        />
      ) : (
        <div className="mnote">
          <Note>Первый контакт станет основным.</Note>
        </div>
      )}
    </Modal>
  );
}


/**
 * Добавить команду в студию (fAddTeam, доработка M2-1 п. 5): найти
 * существующую среди тех, что не в этой студии, или завести новую.
 * Команда из другой студии переходит с сегодняшнего дня — прошлое
 * остаётся за прежней студией (БЛ M2 5.11), об этом заметка.
 */
export function AddTeamModal({
  studio,
  onClose,
  onNew,
}: {
  studio: StudioCard;
  onClose: () => void;
  onNew: () => void;
}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const q = useDebounced(text).trim();
  const list = useTeamList({ q, limit: 6, notInStudio: studio.id }, q.length >= 2);
  const items = list.data?.items ?? [];
  const chosen = items.find((t) => t.id === teamId) ?? null;
  const add = useBaseMutation(() => api.post(`/studios/${studio.id}/teams`, { teamId }));

  return (
    <Modal
      title={`Добавить команду в студию «${studio.name}»`}
      size="wide"
      onClose={onClose}
      confirm={{
        label: 'Добавить',
        disabled: !chosen || add.isPending,
        onClick: () =>
          add.mutate(undefined, {
            onSuccess: () => {
              toast.show('Команда добавлена в студию');
              onClose();
            },
            onError: (e) => toast.showError(errorText(e)),
          }),
      }}
    >
      <ChoiceField
        label="Команда"
        items={[
          { value: 'find', label: 'Найти в базе' },
          { value: 'new', label: 'Новая команда' },
        ]}
        value="find"
        onChange={(v) => (v === 'new' ? onNew() : undefined)}
      />
      <TextField
        label="Поиск"
        placeholder="название, город или руководитель"
        value={text}
        onValue={(v) => {
          setText(v);
          setTeamId(null);
        }}
        autoFocus
      />
      {q.length < 2 ? (
        <FieldHint gap>Начните вводить — покажем команды, которые ещё не в этой студии.</FieldHint>
      ) : items.length ? (
        <OptionList
          value={teamId}
          onChange={setTeamId}
          options={items.map((t) => ({
            value: t.id,
            title: t.name,
            description: [
              t.city.name,
              `рук. ${t.head.shortName}`,
              t.studio ? `сейчас в студии «${t.studio.name}»` : 'без студии',
            ].join(' · '),
          }))}
        />
      ) : list.isFetching ? null : (
        <FieldHint gap>
          Не нашли. <TextLink onClick={onNew}>Создать новую команду</TextLink>
        </FieldHint>
      )}
      {chosen?.studio ? (
        <div className="mnote">
          <Note tone="warn">
            Команда перейдёт из студии «{chosen.studio.name}» с сегодняшнего дня. Прошлые выступления и счета
            останутся за «{chosen.studio.name}».
          </Note>
        </div>
      ) : null}
    </Modal>
  );
}
